import { SuggestionsType } from '@analytics/const';
import { Injectable, Injector } from '@angular/core';
import { ProductListFacade } from '@catalog/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ProductListSmartFoundService } from '@search/feature';
import { Breadcrumb, CmsService, Page, RoutingService } from '@spartacus/core';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { ProductListAnalyticsService } from './product-list-analytics.service';

@Injectable({ providedIn: 'root' })
export class ProductListPageAnalyticsService extends ProductListAnalyticsService {
  private pageName$: Observable<string>;

  constructor(
    private productListFacade: ProductListFacade,
    private routingService: RoutingService,
    private cmsService: CmsService,
    private productListSmartFoundService: ProductListSmartFoundService,
    injector: Injector,
  ) {
    super(injector);
    this.setAnalyticsListName();
  }

  private setAnalyticsListName(): void {
    this.pageName$ = this.cmsService.getCurrentPage().pipe(
      filter(Boolean),
      map((page: Page) => page.name || page.title),
    );

    this.subscription.add(
      combineLatest({
        pagination: this.productListFacade.getPagination(),
        breadcrumbs: this.productListFacade.getBreadcrumbs(),
        routerState: this.routingService
          .getRouterState()
          .pipe(filter((routerState) => Boolean(routerState.state.semanticRoute))),
        pageName: this.pageName$,
        isSmartSearchEnabled: this.productListSmartFoundService.isSmartSearchEnabled().pipe(startWith(false)),
      })
        .pipe(
          map(({ pagination, breadcrumbs, routerState, pageName, isSmartSearchEnabled }) => {
            const pageN = (pagination?.currentPage || 0) + 1;
            const queryParams = routerState.state.queryParams || {};
            const params = routerState.state.params || {};
            const freeTextSearch: string | undefined = queryParams.freeTextSearch || params.query;
            const suggestionType: SuggestionsType | undefined = queryParams.suggestionType;

            switch (routerState.state.semanticRoute) {
              case 'category': {
                return this.formatCategoryListName({
                  searchText: freeTextSearch,
                  suggestionType,
                  breadcrumbs,
                  pageN,
                  isSmartSearchEnabled,
                });
              }

              case 'search': {
                const [lastBreadcrumb] = breadcrumbs.slice(-1);
                const searchText = lastBreadcrumb?.facetValueName || freeTextSearch;

                if (!searchText) {
                  return '';
                }

                return this.formatSearchListName({
                  suggestionType,
                  searchText,
                  pageN,
                  isSmartSearchEnabled,
                });
              }
              case 'saleProductList':
                return `Распродажа_${pageN}`;

              case 'bestOffersProductList':
                return `Best_main_${pageN}`;

              case 'seoProductList':
                return `Promo_${params.tagSlug}_${pageN}`;

              case 'promoProductList':
                return `Promo_${pageName}_${pageN}`;

              default:
                return '';
              // Остальные кейсы дописать по мере выполнения задач
            }
          }),
        )
        .subscribe((val) => {
          this.analyticsListName$.next(val);
        }),
    );
  }

  private formatSearchListName({
    suggestionType,
    searchText,
    pageN,
    isSmartSearchEnabled,
  }: {
    suggestionType: SuggestionsType;
    searchText: string;
    pageN: number;
    isSmartSearchEnabled?: boolean;
  }): string {
    const nSearchText = searchText.length > 100 ? 'LongString' : searchText;

    if (isSmartSearchEnabled) {
      return `ПоискНулевой_${nSearchText}_${pageN}`;
    }

    if (suggestionType) {
      return `SearchHelper_${suggestionType}_${nSearchText}_${pageN}`;
    }

    return `Поиск_${nSearchText}_${pageN}`;
  }

  private formatCategoryListName({
    searchText,
    suggestionType,
    breadcrumbs,
    pageN,
    isSmartSearchEnabled,
  }: {
    searchText?: string;
    suggestionType: SuggestionsType;
    breadcrumbs: Breadcrumb[];
    pageN: number;
    isSmartSearchEnabled?: boolean;
  }): string {
    const getCode = (categoryCode: string): string => categoryCode.split('-').slice(-1)[0];

    if (!breadcrumbs.length) {
      return '';
    }

    // прямой переход на страницу категории из поиска
    if (searchText) {
      return this.formatSearchListName({ suggestionType, searchText, pageN, isSmartSearchEnabled });
    }

    // переход на страницу категории из подсказки в поиске
    if (suggestionType) {
      const [lastBreadcrumb] = breadcrumbs.slice(-1);

      return `SearchHelper_${suggestionType}_${lastBreadcrumb.facetValueName}_${pageN}`;
    }

    // обычный переход на страницу категории
    if (breadcrumbs[0].facetCode === 'allCategories') {
      const ids = breadcrumbs.map((br) => (br.facetValueCode ? getCode(br.facetValueCode) : '')).join('/');

      return `Каталог_${ids}_${pageN}`;
    }

    // переход на страницу корневого каталог с зажатым фильтром
    const facets = breadcrumbs.map((br) => `${br.facetName}_${br.facetValueName}`).join('/');

    return `Каталог_${facets}_${pageN}`;
  }
}
